'use client';

import { cn } from '@savvy-kit/lib/utils';
import InputField from '@savvy-kit/patterns/input-field';
import { useController, useFormContext } from 'react-hook-form';
import { formatManualDollarAmountEntry } from 'src/utils/formatting';

type MonetaryInputFieldProps = {
  readonly name: string;
  readonly label?: string;
  readonly nullable?: boolean;
  readonly allowDecimals?: boolean;
  readonly fullWidth?: boolean;
  readonly className?: string;
  readonly inputClassName?: string;
};

/*
 * Currency input field that only takes in numeric values.
 * Cannot add negatives; decimals are optional via allowDecimals.
 *
 * Use within a react-hook-form FormProvider context.
 *
 * Example usage:
 *
 * const methods = useForm<FormType>();
 * return (
 *   <FormProvider {...methods}>
 *     <MonetaryInputField name="formFieldName" label="Amount" />
 *   </FormProvider>
 * )
 */
export function MonetaryInputField({
  name,
  label,
  nullable = false,
  allowDecimals = false,
  fullWidth = false,
  className,
  inputClassName,
}: MonetaryInputFieldProps) {
  const { trigger } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({ name });

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      <InputField
        id={name}
        label={label}
        className={inputClassName}
        value={formatManualDollarAmountEntry(field.value)}
        onChange={(e) => {
          const numericValue = allowDecimals
            ? e.target.value.replace(/[^0-9.]/g, '')
            : e.target.value.replace(/[^0-9]/g, '');
          const fieldValue = nullable && numericValue === '' ? null : numericValue;
          field.onChange(fieldValue);
          if (error) {
            trigger(name);
          }
        }}
        prependInputAddon="$"
        error={!!error}
        onBlur={field.onBlur}
        errorMessage={error?.message}
      />
    </div>
  );
}
